import React, { ReactNode, useState } from 'react';
import styled, { css } from 'styled-components';

import {
  BUTTON_THEMES,
  CARD_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import { ICard } from '../../../types/shared';
import { VIEW_TYPES } from '../../organisms/navigation/shared/constants';

import ChevronIconAnimation from '../../atoms/icons/ChevronIconAnimation';
import { ChevronRight } from '../../atoms/icons/ChevronRight';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, useDeviceType } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';

import mq from '../../../styles/breakpoints';
import colors from '../../../styles/colors';
import { Body, H3, IntroText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface FeatureBodyProps {
  featuredItem: ICard;
  deviceType: string;
  activeFeatureId?: string;
  animateItem?: boolean;
  children?: ReactNode;
  className?: string;
}

const FeatureBody = ({
  featuredItem,
  deviceType,
  activeFeatureId,
  animateItem,
  children,
  className,
}: FeatureBodyProps) => {
  if (activeFeatureId !== featuredItem.id) return;

  const productPrimaryLink = featuredItem.primaryLink
    ? getCtaLink(featuredItem.primaryLink)
    : undefined;
  const productSecondaryLink = featuredItem.secondaryLink
    ? getCtaLink(featuredItem.secondaryLink)
    : undefined;

  return (
    <FeatureBodyContainer
      data-testid="feature"
      id={featuredItem.id}
      open={activeFeatureId === featuredItem.id}
      animateTransition={animateItem}
      className={className}
    >
      {deviceType === VIEW_TYPES.desktop && <H3>{featuredItem.title}</H3>}
      {featuredItem.subtitle && (
        <StyledIntroText>{featuredItem.subtitle}</StyledIntroText>
      )}
      <Body>
        {children}
        {(featuredItem.primaryLink || featuredItem.secondaryLink) && (
          <CtaWrapper>
            {featuredItem.primaryLink && (
              <StyledPrimaryButton
                buttonTheme={
                  featuredItem.primaryLink.theme || BUTTON_THEMES.default
                }
                to={productPrimaryLink}
                className="button"
                deqComponentTitle={featuredItem.primaryLink.label}
                type={featuredItem.primaryLink.type}
                icon={ICON_TYPES.none}
                label={featuredItem.primaryLink.label}
                deqCustomType="sales-block"
                deqCustomAttributes={{
                  componentType: 'sales-block',
                  type: 'btn-base',
                  elementTarget: productPrimaryLink,
                }}
              />
            )}
            {featuredItem.secondaryLink && (
              <SecondaryButton
                to={productSecondaryLink}
                className="link"
                deqComponentTitle={featuredItem.secondaryLink.label}
                label={featuredItem.secondaryLink.label}
                icon={featuredItem.secondaryLink.icon}
                type={featuredItem.secondaryLink.type}
                iconPosition={featuredItem.secondaryLink.iconPosition}
                deqCustomAttributes={{
                  componentType: 'sales-block',
                  type: DEQ_TYPES.textLink,
                  label: featuredItem.secondaryLink.label,
                  elementTarget: productSecondaryLink,
                }}
              />
            )}
          </CtaWrapper>
        )}
      </Body>
    </FeatureBodyContainer>
  );
};

interface ProductFeatureProps {
  onChange: (event: { open: boolean; id: string }) => void;
  animateItem?: boolean;
  features: ICard[];
  activeFeatureId?: string;
}

const ProductFeature = ({
  onChange,
  animateItem = true,
  features,
  activeFeatureId,
}: ProductFeatureProps) => {
  const device = useDeviceType();
  const [isActiveCard, setIsActiveCard] = useState(features[0]);

  const handleOnChange = (isOpen: boolean, labelItem: ICard) => {
    onChange({ open: !isOpen, id: labelItem.id });
    setIsActiveCard(labelItem);
  };

  return (
    <Container cardAmount={features.length}>
      <CardContainer>
        {features?.map((item: ICard, key: number) => (
          <LabelWrapper
            key={key}
            data-component-type="tab-toggle-block"
            data-element-type={DEQ_TYPES.btnTab}
            data-element-label={item.title?.toLowerCase()}
            data-testid="product-features-label"
            onClick={() => handleOnChange(activeFeatureId === item.id, item)}
            role="button"
            isActive={activeFeatureId === item.id}
          >
            <InnerLabelWrapper>
              {item.title && (
                <LabelCopy isActive={activeFeatureId === item.id}>
                  {item.title}
                </LabelCopy>
              )}
              <StyledChevronIconAnimation
                open={activeFeatureId === item.id}
                animateTransition={animateItem}
              />
              <StyledChevronRight className="end animate-icon" />
            </InnerLabelWrapper>
            {(device === VIEW_TYPES.mobile || device === VIEW_TYPES.tablet) && (
              <FeatureBody
                deviceType={device}
                featuredItem={item}
                animateItem={animateItem}
                activeFeatureId={activeFeatureId}
              >
                <ReactMarkdownExtended
                  componentType={CARD_TYPES.productFeature}
                >
                  {item.body.childMarkdownRemark.rawMarkdownBody}
                </ReactMarkdownExtended>
              </FeatureBody>
            )}
          </LabelWrapper>
        ))}
      </CardContainer>
      {device === VIEW_TYPES.desktop && (
        <FeatureBody
          deviceType={device}
          featuredItem={isActiveCard}
          animateItem={animateItem}
          activeFeatureId={activeFeatureId}
        >
          <ReactMarkdownExtended componentType={CARD_TYPES.productFeature}>
            {isActiveCard.body.childMarkdownRemark.rawMarkdownBody}
          </ReactMarkdownExtended>
        </FeatureBody>
      )}
    </Container>
  );
};

export default ProductFeature;

const Container = styled.div<{ cardAmount: number }>`
  display: grid;
  grid-template-columns: minmax(100%, 1fr);
  width: 100%;

  ${mq.from.breakpoint.L`
    grid-column-gap: 48px;
    grid-template-columns: minmax(300px, 1fr) 2fr;
    grid-template-rows: repeat(${({ cardAmount }: { cardAmount?: number }) =>
      cardAmount}, minmax(1fr, 104px));
  `}

  ${mq.from.breakpoint.XL`
    grid-template-columns: minmax(408px, 1fr) 2fr;
  `}
`;

const CardContainer = styled.div`
  background-color: ${colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationWell};
  height: fit-content;
  margin-left: -16px;
  margin-right: -16px;
  padding: 0;

  ${mq.from.breakpoint.M`
    margin-left: -24px;
    margin-right: -24px;
  `}

  ${mq.from.breakpoint.L`
    margin-left: 0;
    margin-right: 0;
    border-radius: ${({ theme }) => theme.radiuses.M};
    box-shadow: ${({ theme }) => theme.elevations.abnElevationCard};
    padding: ${Spacings.M} 0;
  `}
`;

const LabelWrapper = styled.div<{ isActive: boolean }>`
  cursor: pointer;
  margin: 0;
  padding: ${Spacings.S};

  &:not(:last-of-type) {
    border-bottom: 1px solid ${colors.abnNeutrals75};
  }

  ${mq.from.breakpoint.L`
    margin-bottom: 8px;
    background-color: ${({ isActive }: { isActive: boolean }) =>
      isActive ? colors.abnGreen55 : colors.white};
    padding: ${Spacings.S} ${Spacings.M} ${Spacings.S} ${Spacings.L};

    &:not(:last-of-type) {
      border-bottom: none;
    }

    &:last-of-type {
      margin-bottom: 0;
    }

    &:hover,
    &:active,
    &:focus {
      background-color: ${colors.abnMutedGreen50};

      svg.animate-icon {
        transform: translateX(4px);
      }
    }

    ${({ isActive }: { isActive: boolean }) =>
      isActive &&
      css`
        svg.animate-icon {
          transform: translateX(4px);
        }
      `}
  `}
`;

const InnerLabelWrapper = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.colors.abnGreen300};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  justify-items: center;

  ${mq.from.breakpoint.L`
    &:hover {
      color: ${({ theme }) => theme.colors.abnGreen200};
    }
  `}
`;

const StyledChevronIconAnimation = styled(ChevronIconAnimation)`
  display: block;
  flex-shrink: 0;

  ${mq.from.breakpoint.L`
    display: none;
  `}
`;

const StyledChevronRight = styled(ChevronRight)`
  display: none;
  flex-shrink: 0;

  ${mq.from.breakpoint.L`
    display: block;
  `}
`;

const LabelCopy = styled(Body)<{ isActive: boolean }>`
  font-weight: 500;

  ${mq.from.breakpoint.L`
    font-weight: ${({ isActive }: { isActive: boolean }) =>
      isActive ? '500' : '400'};
  `}
`;

const StyledIntroText = styled(IntroText)`
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;

  ${mq.from.breakpoint.L`
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
  `}
`;

const CtaWrapper = styled.div`
  margin-top: ${Spacings.L};
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  margin-bottom: ${Spacings.M};
  margin-right: 0;

  ${mq.from.breakpoint.M`
    margin-bottom: 0;
    margin-right: ${Spacings.L};
  `}
`;

const FeatureBodyContainer = styled.div<{
  open: boolean;
  type?: string;
  animateTransition?: boolean;
}>`
  margin: ${Spacings.M} 0 ${Spacings.XS};
  max-height: 0;
  overflow: hidden;
  padding: 0;

  ${({ open }) =>
    open &&
    css`
      max-height: 2000px;
      min-height: 90%;
    `}

  ${({ animateTransition }) =>
    animateTransition &&
    css`
      transition: max-height 0.3s ease-in-out;
    `}
  
  ${mq.from.breakpoint.L`
    margin: 0;
  `}
`;
